import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth, db, checkDbStatus } from '../firebase';
import { doc, getDoc, setDoc, deleteDoc, onSnapshot, query, collection, where, getDocs, updateDoc } from 'firebase/firestore';

interface FirebaseContextType {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  userProfile: any | null;
  dbConnected: boolean;
}

const FirebaseContext = createContext<FirebaseContextType>({
  user: null,
  loading: true,
  isAdmin: false,
  userProfile: null,
  dbConnected: false
});

export const useFirebase = () => useContext(FirebaseContext);

export const FirebaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userProfile, setUserProfile] = useState<any | null>(null);
  const [dbConnected, setDbConnected] = useState(false);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const connected = await checkDbStatus();
        setDbConnected(connected);
      } catch (err) {
        console.error("Failed to check db status", err);
      }
    };
    checkConnection();

    let unsubscribeProfile: () => void = () => {};
    let unsubscribePending: () => void = () => {};

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      unsubscribeProfile(); // Ensure we unsubscribe from previous listener
      unsubscribePending(); // Ensure we unsubscribe from pending memberships
      setUser(currentUser);
      
      if (currentUser) {
        // Real-time listener for user profile
        const userDocRef = doc(db, 'users', currentUser.uid);
        
        const setupListener = () => {
          unsubscribeProfile = onSnapshot(userDocRef, (docSnap) => {
            if (docSnap.exists()) {
              const profile = docSnap.data();
              setUserProfile(profile);
              setIsAdmin(profile.role === 'admin' || currentUser.email?.toLowerCase() === 'tshark1975@gmail.com');
            } else {
              // If document is later deleted while logged in, reset profile
              setUserProfile(null);
              setIsAdmin(currentUser.email?.toLowerCase() === 'tshark1975@gmail.com'); // Admin usually retains access 
            }
            setLoading(false);
          }, (error) => {
            console.error("FirebaseContext: Profile snapshot error", error);
            setLoading(false);
          });
        };

        // Check and create profile if needed
        const checkOrCreateProfile = async () => {
          try {
            const docSnap = await getDoc(userDocRef);
            const isInitialAdmin = currentUser.email?.toLowerCase() === 'tshark1975@gmail.com';
            const emailKey = currentUser.email?.toLowerCase();
            
            if (!docSnap.exists()) {
              setLoading(true);
              let status: 'active' | 'inactive' | 'pending' = 'inactive';
              let tier = 'Non-Member';
              let mId = '';
              let role: 'admin' | 'member' | 'user' = isInitialAdmin ? 'admin' : 'user';
              let isVerified = isInitialAdmin;
              let dName = currentUser.displayName;
              let initialFinancials = {
                initialPurchaseDate: new Date().toISOString(),
                lifetimeSpend: 0
              };
              
              if (!isInitialAdmin && emailKey) {
                try {
                  const pendingRef = doc(db, 'pending_memberships', emailKey);
                  const pendingDoc = await getDoc(pendingRef);
                  if (pendingDoc.exists()) {
                    const pendingData = pendingDoc.data();
                    status = 'active';
                    tier = pendingData.membershipTier || pendingData.planName || 'Syndicate Member';
                    mId = pendingData.membershipId || pendingData.planId || '';
                    role = 'member';
                    isVerified = true;
                    if (!dName && pendingData.clientName) {
                      dName = pendingData.clientName;
                    }
                    if (pendingData.financials) {
                      initialFinancials = {
                        ...initialFinancials,
                        ...pendingData.financials
                      };
                    }
                    await deleteDoc(pendingRef);
                    console.log("FirebaseContext: Claimed pending membership for new user", emailKey);
                  }
                } catch (pendingErr) {
                  console.error("FirebaseContext: Error checking pending statuses", pendingErr);
                }
              } else if (isInitialAdmin) {
                status = 'active';
                tier = 'Syndicate Member';
                mId = 'founder-access';
                role = 'admin';
                isVerified = true;
              }

              const initialProfile = {
                uid: currentUser.uid,
                email: currentUser.email,
                displayName: dName,
                role: role,
                membershipTier: tier as any,
                membershipId: mId,
                membershipStatus: status,
                automationTags: tier === 'Syndicate Member' ? ['SYNDICATE_MEMBER'] : tier === 'Elite Status' ? ['ELITE_RANK'] : [],
                hardwareCredit: false,
                isVerified: isVerified,
                joinedAt: new Date().toISOString(),
                bookings: [],
                financials: initialFinancials
              };

              await setDoc(userDocRef, initialProfile);
              console.log("FirebaseContext: Created initial profile");
            } else {
              // Existing user
              const existingProfile = docSnap.data();
              
              // Force admin role for the initial admin email if not set
              if (isInitialAdmin && existingProfile?.role !== 'admin') {
                await setDoc(userDocRef, { role: 'admin', isVerified: true, membershipTier: 'Founder' }, { merge: true });
                console.log("FirebaseContext: Forced admin role for initial admin");
              }

              // check if they have a pending membership to claim
              if (emailKey && !isInitialAdmin) {
                try {
                  const pendingRef = doc(db, 'pending_memberships', emailKey);
                  const pendingDoc = await getDoc(pendingRef);
                  if (pendingDoc.exists()) {
                    const pendingData = pendingDoc.data();
                    const existingProfile = docSnap.data();
                    
                    // Only update if they aren't already active or different tier
                    const pendingMId = pendingData.membershipId || pendingData.planId || '';
                    if (existingProfile?.membershipStatus !== 'active' || existingProfile?.membershipId !== pendingMId) {
                      const tags = existingProfile?.automationTags || [];
                      const newTier = pendingData.membershipTier || pendingData.planName || 'Syndicate Member';
                      if (newTier === 'Syndicate Member' && !tags.includes('SYNDICATE_MEMBER')) tags.push('SYNDICATE_MEMBER');
                      if (newTier === 'Elite Status' && !tags.includes('ELITE_RANK')) tags.push('ELITE_RANK');

                      const updateData: any = {
                        membershipStatus: 'active',
                        membershipTier: newTier,
                        membershipId: pendingMId,
                        automationTags: tags,
                        hardwareCredit: false,
                        role: 'member',
                        isVerified: true
                      };

                      if (pendingData.financials) {
                        updateData.financials = {
                          ...existingProfile?.financials,
                          ...pendingData.financials,
                          lifetimeSpend: (existingProfile?.financials?.lifetimeSpend || 0) + (pendingData.financials.lifetimeSpend || 0)
                        };
                      }

                      await setDoc(userDocRef, updateData, { merge: true });
                      await deleteDoc(pendingRef);
                      console.log("FirebaseContext: Claimed pending membership for existing user", emailKey);
                    }
                  }
                } catch (pendingErr) {
                  console.error("FirebaseContext: Error checking pending statuses for existing user", pendingErr);
                }
              }
            }

            // New logic: Listen for the pending membership for as long as the user is a non-member
            if (emailKey && !isInitialAdmin) {
              const pendingRef = doc(db, 'pending_memberships', emailKey);
              if (unsubscribePending) unsubscribePending();
              unsubscribePending = onSnapshot(pendingRef, async (pendingSnap) => {
                if (pendingSnap.exists()) {
                  try {
                    const pendingData = pendingSnap.data();
                    console.log("FirebaseContext: Late membership claim detected!", emailKey);
                    const tags = ['SYNDICATE_MEMBER'];
                    const newTier = pendingData.membershipTier || pendingData.planName || 'Syndicate Member';
                    if (newTier === 'Elite Status') {
                      tags[0] = 'ELITE_RANK';
                    }

                    const updateData: any = {
                      membershipStatus: 'active',
                      membershipTier: newTier,
                      membershipId: pendingData.membershipId || pendingData.planId || '',
                      automationTags: tags,
                      hardwareCredit: false,
                      role: 'member',
                      isVerified: true
                    };

                    if (pendingData.financials) {
                      const currentSnap = await getDoc(userDocRef);
                      const currentData = currentSnap.data();
                      updateData.financials = {
                        ...currentData?.financials,
                        ...pendingData.financials,
                        lifetimeSpend: (currentData?.financials?.lifetimeSpend || 0) + (pendingData.financials.lifetimeSpend || 0)
                      };
                    }

                    await setDoc(userDocRef, updateData, { merge: true });
                    await deleteDoc(pendingRef);
                    unsubscribePending(); // Stop listening once claimed
                  } catch (e) {
                    console.error("FirebaseContext: Error handling late membership claim", e);
                  }
                }
              }, (err) => console.error("FirebaseContext pending snapshot error:", err));
            }

          } catch (err) {
            console.error("FirebaseContext: Failed to check/create profile", err);
          } finally {
            // Automatically claim any guest orders
            if (currentUser.email && currentUser.uid) {
              try {
                // Claim orders
                const ordersQuery = query(collection(db, 'orders'), where('userEmail', '==', currentUser.email));
                const ordersSnap = await getDocs(ordersQuery);
                for (const docSnap of ordersSnap.docs) {
                  if (docSnap.data().userId === 'guest') {
                    await updateDoc(doc(db, 'orders', docSnap.id), { userId: currentUser.uid });
                  }
                }

                // Claim bookings
                const bookingsQuery = query(collection(db, 'bookings'), where('clientEmail', '==', currentUser.email));
                const bookingsSnap = await getDocs(bookingsQuery);
                for (const docSnap of bookingsSnap.docs) {
                  if (docSnap.data().userId === 'guest') {
                    await updateDoc(doc(db, 'bookings', docSnap.id), { userId: currentUser.uid });
                  }
                }
              } catch (claimErr) {
                console.error("Failed to claim guest data", claimErr);
              }
            }
            setupListener();
          }
        };

        checkOrCreateProfile().catch(err => {
          console.error("Unhanded rejection in checkOrCreateProfile", err);
        });
      } else {
        setUserProfile(null);
        setIsAdmin(false);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeProfile();
      unsubscribePending();
      unsubscribe();
    };
  }, []);

  return (
    <FirebaseContext.Provider value={{ user, loading, isAdmin, userProfile, dbConnected }}>
      {children}
    </FirebaseContext.Provider>
  );
};
